import { Link } from "@inertiajs/react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const FREE_FEATURES = [
  "3 free PRs per workspace (lifetime)",
  "Unlimited savings recommendations",
  "Invite teammates",
  "Upgrade to Pro from inside Infralyst at any time",
] as const;

const PRO_FEATURES = [
  "Unlimited PRs",
  "Everything in Free",
  "Priority email support",
  "30-day money-back guarantee",
] as const;

export function Pricing(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Simple, transparent <span className="text-primary">pricing</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          Every workspace starts free with 3 PRs. Upgrade to Infralyst Pro when
          you need more.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Free Plan */}
          <div className="rounded-3xl border bg-background/50 p-6 flex flex-col hover:border-primary/20 hover:bg-primary/5 transition-colors">
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-1">Free</h3>
              <div className="text-3xl font-extrabold mb-2">
                $0
                <span className="text-base text-surface-foreground/60 font-normal">
                  {" "}
                  / workspace
                </span>
              </div>
              <p className="text-sm text-surface-foreground/80">
                Perfect for trying out Infralyst.
              </p>
            </div>

            <ul className="space-y-3 flex-1">
              {FREE_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start text-sm">
                  <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Infralyst Pro Plan */}
          <div className="rounded-3xl border-2 border-primary bg-background/50 p-6 flex flex-col hover:bg-primary/5 transition-colors">
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-1">Infralyst Pro</h3>
              <div className="text-3xl font-extrabold mb-2">
                $99
                <span className="text-base text-surface-foreground/60 font-normal">
                  {" "}
                  / month · per workspace
                </span>
              </div>
              <p className="text-sm text-surface-foreground/80">
                For teams that want automated AWS cost savings in their
                Terraform workflow.
              </p>
            </div>

            <ul className="space-y-3 flex-1">
              {PRO_FEATURES.map((feature, index) => (
                <li key={feature} className="flex items-start text-sm">
                  <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className={index === 0 ? "font-semibold" : ""}>
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-center mt-10">
          <Button asChild size="lg">
            <Link href="/signup">Start free with 3 PRs</Link>
          </Button>
          <p className="text-sm text-surface-foreground/60 mt-4">
            Prefer details first?{" "}
            <Link href="/pricing" className="text-primary hover:underline">
              View full pricing
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
